import React from 'react'
import qz from 'qz-tray'
import $ from 'jquery'

const QZ_CONNECTION_TIMEOUT_MS = 2000
const LOCAL_NETWORK_PERMISSION = 'local-network-access'
const QZ_PERMISSION_WARNING_KEY = 'qz-tray-permission-warning'
const QZ_CONNECTION_WARNING_LIMIT = 5

let hasShownQzConnectionWarning = false
let qzConnectionWarningCount = 0
let qzConnectionPromise = null

const noopSetter = () => {}

const getQzConnectionStateSetter = (setIsConnectionActive) =>
  typeof setIsConnectionActive === 'function' ? setIsConnectionActive : noopSetter

const getQzConnectedResult = () => ({ connected: true, timedOut: false })

const getQzDisconnectedResult = () => ({ connected: false, timedOut: false })

const isQzAvailable = () => qz && qz.websocket && qz.printers

const getQzHostname = async () => {
  if (!qz.networking) {
    return null
  }

  if (typeof qz.networking.hostname === 'function') {
    const hostname = await qz.networking.hostname()
    return hostname || null
  }

  if (typeof qz.networking.device === 'function') {
    const device = await qz.networking.device()
    return device?.hostname ?? null
  }

  return null
}

const getQzConnectionTimeoutResult = () =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve({ connected: false, timedOut: true })
    }, QZ_CONNECTION_TIMEOUT_MS)
  })

const getQzConnectionResult = (connectionPromise) =>
  connectionPromise.then(
    () => ({ connected: true, timedOut: false }),
    () => ({ connected: false, timedOut: false })
  )

const waitForQzConnection = async (connectionPromise) => {
  return Promise.race([getQzConnectionResult(connectionPromise), getQzConnectionTimeoutResult()])
}

const configureQzSecurity = () => {
  qz.security.setCertificatePromise(function (resolve, reject) {
    $.ajax(`${process.env.REACT_APP_API_BASE_URL}/qz-tray/digital-certificate.txt?t=${Date.now()}`).then(
      resolve,
      reject
    )
  })
  qz.security.setSignaturePromise(function (toSign) {
    return async function (resolve, reject) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/apps/qzSign?message=${toSign}`, {
        headers: { 'Content-Type': 'text/plain' }
      })
        .then(function (data) {
          data.ok ? resolve(data.text()) : reject(data.text())
        })
        .catch((err) => {
          console.error('Error in QZ Tray signing message:', err)
        })
    }
  })
}

const connectToQz = async ({ connectionOptions, setIsConnectionActive }) => {
  const setter = getQzConnectionStateSetter(setIsConnectionActive)

  try {
    // Check if qz is available
    if (!isQzAvailable()) {
      console.error('qz library is not properly initialized or available.')
      setter(false)
      return getQzDisconnectedResult()
    }

    configureQzSecurity()

    // Wait up to two seconds for QZ, but keep the underlying connection alive.
    if (!qz.websocket.isActive()) {
      if (!qzConnectionPromise) {
        qzConnectionPromise = qz.websocket.connect(connectionOptions)
          .then(() => {
            setter(true)
            console.log('QZ Tray Connection established!!!', qz.websocket.getConnectionInfo())
          })
          .catch((error) => {
            setter(false)
            console.log(error)
            throw error
          })
          .finally(() => {
            qzConnectionPromise = null
          })
      }

      const result = await waitForQzConnection(qzConnectionPromise)
      setter(result.connected)
      return result
    }

    setter(true)
    console.log('QZ tray connection is already established', qz.websocket.getConnectionInfo())
    return getQzConnectedResult()
  } catch (error) {
    setter(false)
    console.error('Error connecting to QZ Tray:', error)
    return getQzDisconnectedResult()
  } finally {
    console.log('startQZConnection() end')
  }
}

const getLocalNetworkPermissionState = async () => {
  try {
    if (!navigator?.permissions?.query) {
      return null
    }

    const permission = await navigator.permissions.query({ name: LOCAL_NETWORK_PERMISSION })
    return permission?.state ?? null
  } catch (error) {
    return null
  }
}

const requestLocalNetworkPermission = async (retryConnection) => {
  qzConnectionPromise = null
  const qzPermissionRequest =
    typeof retryConnection === 'function' ? retryConnection() : startQZConnectionForPacking()

  try {
    if (typeof navigator?.permissions?.request === 'function') {
      const permission = await navigator.permissions.request({ name: LOCAL_NETWORK_PERMISSION })
      if (permission?.state === 'denied') {
        return 'denied'
      }
    }
  } catch (error) {
    // Some browsers expose the permission to query but not to request.
  }

  await qzPermissionRequest
  return getLocalNetworkPermissionState()
}

const getSupportLink = (url) =>
  React.createElement(
    'a',
    {
      href: url,
      rel: 'noopener noreferrer',
      target: '_blank'
    },
    url
  )

const getPermissionDeniedDescription = (t) =>
  React.createElement(
    'div',
    null,
    React.createElement(
      'p',
      null,
      t(
        'Allow the "Apps on device" or "Local Network Access" permission. Click on the link below for the device you are using:'
      )
    ),
    React.createElement(
      'p',
      null,
      React.createElement('strong', null, 'Chrome'),
      ' - ',
      getSupportLink('https://support.google.com/chrome/answer/114662')
    ),
    React.createElement(
      'p',
      null,
      React.createElement('strong', null, 'Edge'),
      ' - ',
      getSupportLink(
        'https://support.microsoft.com/en-us/topic/control-a-website-s-access-to-the-local-network-in-microsoft-edge-ef7eff4c-676d-4105-935c-2acbcd841d51'
      )
    ),
    React.createElement(
      'p',
      null,
      React.createElement('strong', null, 'Firefox'),
      ' - ',
      getSupportLink(
        'https://support.mozilla.org/en-US/kb/control-personal-device-local-network-permissions-firefox'
      )
    ),
    React.createElement(
      'p',
      null,
      React.createElement('strong', null, 'Safari'),
      ' - ',
      getSupportLink('https://support.apple.com/en-us/102229')
    )
  )

const getPermissionPromptDescription = (notificationApi, t, retryConnection) =>
  React.createElement(
    'div',
    null,
    React.createElement(
      'p',
      null,
      t('Permission to connect to QZ Tray not granted, '),
      React.createElement(
        'button',
        {
          type: 'button',
          style: {
            background: 'none',
            border: 0,
            color: '#1677ff',
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline'
          },
          onClick: async () => {
            const permissionState = await requestLocalNetworkPermission(retryConnection)
            if (permissionState === 'granted') {
              notificationApi.close?.(QZ_PERMISSION_WARNING_KEY)
            }
          }
        },
        t('press here')
      ),
      t(' to ask for permission.')
    ),
    React.createElement('p', null, React.createElement('strong', null, t('OR'))),
    getPermissionDeniedDescription(t)
  )

const showLimitedQzWarning = (notificationApi, config) => {
  if (qzConnectionWarningCount >= QZ_CONNECTION_WARNING_LIMIT) {
    return false
  }

  qzConnectionWarningCount += 1
  notificationApi['warning'](config)
  return true
}

const showDefaultQzConnectionWarning = (notificationApi, t) => {
  return showLimitedQzWarning(notificationApi, {
    message: t('Warning'),
    description: t(
      'QZ Tray is not able to connect. Check that QZ Tray is running and browser permissions allow local device access.'
    )
  })
}

export const showQzConnectionWarning = async (notificationApi, t, retryConnection) => {
  const permissionState = await getLocalNetworkPermissionState()

  if (permissionState === 'prompt') {
    return showLimitedQzWarning(notificationApi, {
      key: QZ_PERMISSION_WARNING_KEY,
      message: t('Warning'),
      description: getPermissionPromptDescription(notificationApi, t, retryConnection),
      duration: 0
    })
  }

  if (permissionState === 'denied') {
    return showLimitedQzWarning(notificationApi, {
      key: QZ_PERMISSION_WARNING_KEY,
      message: t('Printer and Scales Access Denied'),
      description: getPermissionDeniedDescription(t),
      duration: 0
    })
  }

  return showDefaultQzConnectionWarning(notificationApi, t)
}

export const showQzConnectionWarningOnce = async (notificationApi, t, retryConnection) => {
  if (hasShownQzConnectionWarning) {
    return
  }

  const didShowWarning = await showQzConnectionWarning(notificationApi, t, retryConnection)
  if (didShowWarning) {
    hasShownQzConnectionWarning = true
  }
}

export const startQZConnection = async (serverHost, serverPort) => {
  return connectToQz({
    connectionOptions: {
      host: [serverHost],
      port: {
        secure: [serverPort]
      },
      usingSecure: true,
      retries: 2,
      delay: 0
    }
  })
}

export const startQZConnectionForPacking = async (setIsConnectionActive) => {
  return connectToQz({
    connectionOptions: {
      retries: 0,
      delay: 0
    },
    setIsConnectionActive
  })
}

/**
 * Connect to QZ Tray and resolve the
 * device hostname when possible. Returns a consistent shape so callers can
 * branch on a single value:
 *   { connected: boolean, hostname: string | null, timedOut: boolean }
 *
 * Callers should warn the user when `connected` is false and continue with
 * their existing default/manual flow. This helper never throws.
 */
export const getQzTrayDeviceContext = async (setIsConnectionActive) => {
  const setter = getQzConnectionStateSetter(setIsConnectionActive)

  try {
    const connectionResult = await startQZConnectionForPacking(setter)
    if (!connectionResult.connected) {
      return { connected: false, hostname: null, timedOut: connectionResult.timedOut }
    }
  } catch (error) {
    console.error('Error connecting to QZ Tray:', error)
    setter(false)
    return { connected: false, hostname: null, timedOut: false }
  }

  if (!qz || !qz.websocket || !qz.websocket.isActive()) {
    return { connected: false, hostname: null, timedOut: false }
  }

  try {
    const hostname = await getQzHostname()
    return { connected: true, hostname, timedOut: false }
  } catch {
    return { connected: true, hostname: null, timedOut: false }
  }
}
